import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone } from 'lucide-react';
import GlowingButton from '../ui/GlowingButton';

interface DemoCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DemoCallModal({ isOpen, onClose }: DemoCallModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    phone: '+1 '
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters except '+'
    const digits = value.replace(/[^\d+]/g, '');
    
    // Ensure it starts with +1
    if (!digits.startsWith('+1')) {
      return '+1 ';
    }

    // Format the remaining digits
    let formatted = digits.slice(0, 2); // Keep +1
    const remaining = digits.slice(2);
    
    if (remaining.length > 0) {
      formatted += ' ' + remaining.slice(0, 3);
    }
    if (remaining.length > 3) {
      formatted += ' ' + remaining.slice(3, 6);
    }
    if (remaining.length > 6) {
      formatted += ' ' + remaining.slice(6, 10);
    }

    return formatted;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (formatted.length <= 15) { // +1 XXX XXX XXXX
      setFormData({ ...formData, phone: formatted });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://hook.us2.make.com/fptfhaz8bd4m1lruu2wk5zun3m3zuy8n', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          source: 'str_rebooker_demo',
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        onClose();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative w-full max-w-md mx-auto my-8"
          >
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-[#FFD700] rounded-lg opacity-20 blur" />
              <div className="relative bg-black p-6 sm:p-8 rounded-xl border border-cyan-500/20 shadow-2xl max-h-[90vh] overflow-y-auto">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-300 transition-colors p-3 hover:bg-gray-800 rounded-lg min-h-[48px] min-w-[48px] flex items-center justify-center"
                >
                  <X className="w-6 h-6" />
                </button>

                <div className="flex items-center space-x-4 mb-6">
                  <div className="p-3 bg-cyan-500/10 rounded-lg">
                    <Phone className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-tech text-[#FFD700]">Get Your Demo Call</h3>
                    <p className="text-gray-400 text-sm">From our STR Rebooker Caller</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm sm:text-base font-medium text-gray-300 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full bg-black/50 border border-cyan-500/20 rounded-lg px-4 py-4 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors min-h-[52px] text-base"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm sm:text-base font-medium text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-black/50 border border-cyan-500/20 rounded-lg px-4 py-4 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors min-h-[52px] text-base"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm sm:text-base font-medium text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="+1 XXX XXX XXXX"
                      className="w-full bg-black/50 border border-cyan-500/20 rounded-lg px-4 py-4 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors min-h-[52px] text-base"
                      required
                      pattern="\+1 \d{3} \d{3} \d{4}"
                    />
                    <p className="mt-1 text-xs text-gray-400">Format: +1 XXX XXX XXXX</p>
                  </div>

                  <GlowingButton
                    type="submit"
                    variant="primary"
                    className="w-full min-h-[56px]"
                    disabled={isSubmitting}
                  >
                    <span className="flex items-center justify-center text-[#FFD700] text-base sm:text-lg font-semibold">
                      {isSubmitting ? 'Submitting...' : 'Get Your Demo Call'}
                    </span>
                  </GlowingButton>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}